import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';

import { AppAuthSession, IframeAppAuthSession } from './types';

import { store } from '@/app/store';
import { isIframeWindow } from '@/shared/utils/utils';

const loginRedirectUriKey = 'loginRedirectUri';
export const tokenRefreshThresholdSeconds = 60; // 1 minute

// Store instance will be injected from App.tsx
// to avoid circular dependency issues
// and to use store after it's been created and avoid async
let _store: typeof store;
export const injectStore = (storeInstance: typeof store) => {
  _store = storeInstance;
};

export const cognitoErrorCodes = {
  common: {
    NetworkError: 'NetworkError',
  },
  signIn: {
    NotAuthorizedException: 'NotAuthorizedException',
  },
  fetchAuthSession: {},
};

const amplifyAuthErrorMessages: Record<string, string | undefined> = {
  // signIn
  NotAuthorizedException: 'Incorrect email or password.',
  UserNotFoundException: 'Incorrect email or password.',
  UserNotConfirmedException: 'Account not verified. Please check your email.',
  PasswordResetRequiredException:
    'Password reset required. Please reset your password.',
  UnsupportedSignInStep: 'Unsupported sign in step. Please contact support.',

  // confirmSignIn
  InvalidPasswordException: 'Password does not meet requirements.',

  // confirmResetPassword
  CodeMismatchException: 'Incorrect verification code.',
  ExpiredCodeException:
    'Verification code has expired. Please request a new one.',

  // shared
  LimitExceededException: 'Too many attempts. Please try again later.',
  TooManyRequestsException: 'Too many attempts. Please try again later.',
  NetworkError: 'Network error. Please check your connection.',
} as const;

export const getFriendlyAuthError = (
  error: unknown,
  fallback = 'An unexpected error occurred. Please try again later.'
): string => {
  if (error instanceof Error) {
    return amplifyAuthErrorMessages[error.name] ?? fallback;
  }

  return fallback;
};

export const configureAmplify = () => {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: import.meta.env.VITE_USER_POOL_ID,
        userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      },
    },
  });
};

export function getTokenExpiresIn(expiresAt: number | undefined | null) {
  // Return the remaining time in seconds
  if (!expiresAt) {
    return 0;
  }

  const remainingTimeInSeconds = Math.floor(expiresAt - Date.now() / 1000);
  return remainingTimeInSeconds > 0 ? remainingTimeInSeconds : 0;
}

export function isSessionExpiringSoon(
  expiresAt: number | undefined | null,
  tokenRefreshThreshold: number = tokenRefreshThresholdSeconds
) {
  const expiresIn = getTokenExpiresIn(expiresAt);
  return expiresIn <= tokenRefreshThreshold;
}

export async function getAuthSession(
  forceRefresh = false
): Promise<AppAuthSession | IframeAppAuthSession | null> {
  // iframe app auth
  if (isIframeWindow()) {
    return _store.getState().auth.iframeUser;
  }

  // regular amplify auth session
  try {
    const session = await fetchAuthSession({ forceRefresh });

    if (!session.tokens?.idToken) {
      return null;
    }

    return {
      id_token: session.tokens.idToken.toString(),
      expires_at: session.tokens.idToken.payload.exp,
      token_type: 'Bearer',
    };
  } catch (error) {
    console.error('Failed to get auth session:', error);
    return null;
  }
}

export function getSafeLoginRedirectUri() {
  const uri = sessionStorage.getItem(loginRedirectUriKey) ?? '/';

  if (uri.startsWith('/') && !uri.startsWith('//')) {
    return uri;
  } else {
    return '/';
  }
}

export function setLoginRedirectUri(uri: string) {
  sessionStorage.setItem(loginRedirectUriKey, uri);
}

export function clearLoginRedirectUri() {
  sessionStorage.removeItem(loginRedirectUriKey);
}
